import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Phone, ChevronRight, ArrowLeft, MapPin, Bell } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useI18n } from '../../i18n'

const BLUE = '#46A8F9'
const NAVY = '#15354F'

export default function UrgentScreen() {
  const navigate = useNavigate()
  const { currentCase, updateCase } = useCaseStore()
  const { t } = useI18n()

  const EMERGENCY_SYMPTOMS = useMemo(() => [
    t('urgent.symptomBreathing'),
    t('urgent.symptomChestPain'),
    t('urgent.symptomSevereAllergy'),
    t('urgent.symptomStroke'),
    t('urgent.symptomUncontrolledBleeding'),
    t('urgent.symptomOverdose'),
    t('urgent.symptomHeadInjury'),
    t('urgent.symptomSuicidal'),
  ], [t])

  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => { window.scrollTo(0, 0) }, [])

  // No case → redirect
  useEffect(() => {
    if (!currentCase) navigate('/home', { replace: true })
  }, [currentCase, navigate])

  // Restore previous confirmation state
  useEffect(() => {
    if (currentCase?.urgentScreenCompleted) setConfirmed(true)
  }, [currentCase])

  const handleContinue = () => {
    updateCase({ urgentScreenCompleted: true, hasUrgentSymptoms: false })
    navigate('/case/describe/voice')
  }

  if (!currentCase) return null

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* Header — app-consistent back + bell */}
      <div className="px-4 pt-4 pb-2 flex-shrink-0 bg-white">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 text-gray-400 active:text-gray-600 transition-colors">
            <ArrowLeft size={22} />
          </button>
          <button onClick={() => navigate('/notifications')} className="p-1.5 -mr-1.5 text-gray-400">
            <Bell size={20} />
          </button>
        </div>
      </div>

      <div className="px-5 pb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0 border border-red-100">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <h1 className="text-[18px] font-extrabold" style={{ color: NAVY }}>{t('urgent.safetyNotice')}</h1>
            <p className="text-[12px] text-gray-400">{t('urgent.thisAppIsNotFor')}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 overflow-auto pt-3">
        <p className="text-[13px] text-gray-600 leading-relaxed mb-4">
          {t('urgent.stopAndLead')}{' '}
          <strong className="text-red-600">{t('urgent.call911Lower')}</strong> {t('urgent.immediatelyColon')}
        </p>

        {/* Two-column symptom grid */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-4">
          {EMERGENCY_SYMPTOMS.map((s, i) => (
            <div key={i} className="flex items-start gap-2 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-[7px]" />
              <span className="text-[12px] text-gray-700 leading-snug">{s}</span>
            </div>
          ))}
        </div>

        {/* Emergency CTA - compact */}
        <a
          href="tel:911"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-[12px] font-semibold mb-6 active:bg-red-100 transition-colors"
        >
          <Phone size={14} />
          {t('urgent.call911OrFindEr')}
        </a>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{t('urgent.confirmToProceed')}</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Toggle confirmation */}
        <button
          onClick={() => setConfirmed(!confirmed)}
          className={`w-full rounded-2xl p-5 flex items-center gap-4 text-left transition-all duration-300 ${
            confirmed
              ? 'bg-green-50 border-[2px] border-green-400 shadow-sm shadow-green-100'
              : 'bg-gray-50 border-[2px] border-dashed border-gray-300'
          }`}
        >
          {/* Toggle pill */}
          <div className={`w-12 h-7 rounded-full p-[3px] flex items-center transition-all duration-300 flex-shrink-0 ${
            confirmed ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'
          }`}>
            <div className="w-[22px] h-[22px] rounded-full bg-white shadow-sm" />
          </div>
          <div>
            <p className={`text-[14px] font-bold leading-snug transition-colors ${confirmed ? 'text-green-700' : 'text-gray-500'}`}>
              {confirmed ? 'Confirmed — no emergency symptoms' : 'Tap to confirm you are safe to proceed'}
            </p>
            <p className={`text-[11px] mt-0.5 transition-colors ${confirmed ? 'text-green-500' : 'text-gray-400'}`}>
              {t('urgent.notExperiencingAbove')}
            </p>
          </div>
        </button>
      </div>

      {/* Bottom button */}
      <div className="p-5 pb-7 flex-shrink-0">
        <button
          onClick={confirmed ? handleContinue : undefined}
          disabled={!confirmed}
          className={`w-full py-3.5 rounded-full font-bold text-[15px] flex items-center justify-center gap-2 transition-all duration-300 ${
            confirmed
              ? 'text-white active:scale-[0.98] breathe-blue'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          style={confirmed ? { backgroundColor: BLUE } : undefined}
        >
          {t('emergency.startCase')}
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  )
}
